/**
 * Learning Dashboard — /app/renata/learning
 * Supervised only — never auto-apply.
 */
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Sparkles, RefreshCw, Check, X, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";

export const Route = createFileRoute("/app/renata/learning")({
  component: LearningPage,
  head: () => ({ meta: [{ title: "Aprendizajes — Renata" }] }),
});

type Candidate = {
  id: string;
  category: string;
  status: string;
  reason: string;
  confidence: number;
  source: string;
};

function LearningPage() {
  const tenantId = useWorkspaceTenantId();
  const [stats, setStats] = useState<Record<string, number>>({});
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [quality, setQuality] = useState<Record<string, number>>({});
  const [decay, setDecay] = useState<Array<Record<string, unknown>>>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    if (!tenantId) return;
    setBusy(true);
    try {
      const res = await fetch("/api/renata/learning?view=overview", {
        headers: clientRequestHeaders(tenantId),
      });
      const data = await res.json();
      setStats(data.stats ?? {});
      setCandidates(data.candidates ?? []);
      setQuality(data.quality ?? {});
      setDecay(data.decay ?? []);
    } finally {
      setBusy(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function post(action: string, body: Record<string, unknown> = {}) {
    if (!tenantId) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch("/api/renata/learning", {
        method: "POST",
        headers: {
          ...clientRequestHeaders(tenantId),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action, ...body }),
      });
      const data = await res.json();
      setMsg(data.message ?? data.stage ?? (res.ok ? "ok" : data.error ?? "error"));
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <RenataPageChrome screenId="learning" title="Aprendizajes" subtitle="Mejoras propuestas. Nunca se aplican solas." />
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Sparkles className="h-6 w-6" />
            Aprendizajes
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Aprendizaje supervisado. Nunca auto-aplica. Todo pasa por revisión y aprobación.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
          <RefreshCw className={`mr-1 h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </header>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {([
          ["candidate", "Candidatos"],
          ["review", "En revisión"],
          ["approved", "Aprobados"],
          ["rejected", "Rechazados"],
        ] as const).map(([k, label]) => (
          <div key={k} className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold">{stats[k] ?? 0}</div>
          </div>
        ))}
      </section>

      <section className="rounded-lg border p-4 space-y-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <FlaskConical className="h-4 w-4" /> Estado de aprendizajes
        </h2>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="secondary">Precisión {quality.precision ?? 0}</Badge>
          <Badge variant="secondary">Cobertura {quality.recall ?? 0}</Badge>
          <Badge variant="secondary">Aceptados {(quality.accepted_pct ?? 0) * 100 | 0}%</Badge>
          <Badge variant="secondary">Rechazados {(quality.rejected_pct ?? 0) * 100 | 0}%</Badge>
          <Badge variant="secondary">Crecimiento {quality.knowledge_growth ?? 0}</Badge>
          <Badge variant="outline">Por revisar {decay.length}</Badge>
        </div>
      </section>

      {msg ? <p className="text-sm text-muted-foreground">{msg}</p> : null}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Cola de aprendizajes</h2>
        {candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin candidatos todavía.</p>
        ) : (
          candidates.map((c) => (
            <div key={c.id} className="rounded-lg border p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge>{c.status}</Badge>
                <Badge variant="outline">{c.category}</Badge>
                <span className="text-xs text-muted-foreground">
                  confianza {(c.confidence * 100).toFixed(0)}% · {c.source}
                </span>
              </div>
              <p className="text-sm">{c.reason}</p>
              <div className="flex flex-wrap gap-2">
                {c.status === "candidate" || c.status === "draft" ? (
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("review", { candidateId: c.id })}>
                    Enviar a revisión
                  </Button>
                ) : null}
                {c.status === "review" || c.status === "candidate" ? (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => void post("approve", { candidateId: c.id, applyKnowledge: true })}>
                      <Check className="mr-1 h-3.5 w-3.5" /> Aprobar
                    </Button>
                    <Button size="sm" variant="destructive" disabled={busy} onClick={() => void post("reject", { candidateId: c.id, reason: "Rechazado en dashboard" })}>
                      <X className="mr-1 h-3.5 w-3.5" /> Rechazar
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
